import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { downloadFile } from "../../lib/download";
import { useAuth } from "../../context/AuthContext";
import { Card, Btn, Empty } from "../../components/UI";
import { FLabel, FInput } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { ShieldAlert, Download, FileText, Eye, ChevronLeft, ChevronRight } from "lucide-react";

const MODES = [["counts", "Counts by diagnosis", "No client names. Any clinician can export."], ["list", "Client list with names", "Identified. Admins, or clinicians when an admin allows it."]];

function ClinicianToggle({ on, onChange }) {
  return (
    <label className="flex items-start gap-3 rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 text-sm text-[#3E2723]" data-testid="ph-clinician-toggle-wrap">
      <input type="checkbox" data-testid="ph-clinician-toggle" checked={on} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#4E342E]" />
      <span><span className="font-semibold">Let clinicians export the client list with names</span>
        <span className="block text-xs text-[#6D4C41]">Admin setting. Off by default. Every export is recorded in the audit trail.</span></span>
    </label>
  );
}

const PAGE = 50;

function Pager({ page, total, setPage }) {
  const pages = Math.ceil(total / PAGE);
  if (pages < 2) return null;
  return (
    <div className="flex items-center justify-between gap-3 border-t border-[#EFEBE0] bg-[#FDFCF8] px-3 py-2 text-xs text-[#6D4C41]" data-testid="ph-pager">
      <span data-testid="ph-page-info">Rows {page * PAGE + 1} to {Math.min(total, (page + 1) * PAGE)} of {total}</span>
      <div className="flex gap-1.5">
        <Btn variant="ghost" data-testid="ph-page-prev" disabled={page === 0} onClick={() => setPage(page - 1)}><ChevronLeft size={13} /> Previous</Btn>
        <Btn variant="ghost" data-testid="ph-page-next" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>Next <ChevronRight size={13} /></Btn>
      </div>
    </div>
  );
}

function Preview({ data }) {
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [data]);
  if (!data) return null;
  if (!data.rows.length) return <Empty text="No diagnoses match these filters." />;
  const start = page * PAGE;
  return (
    <div className="overflow-hidden rounded-2xl border border-[#EFEBE0]" data-testid="ph-preview">
      <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="bg-[#F5F2EB] text-left text-[10px] uppercase text-[#6D4C41]">{data.columns.map(([k, l]) => <th key={k} className="whitespace-nowrap px-3 py-2">{l}</th>)}</tr></thead>
        <tbody>{data.rows.slice(start, start + PAGE).map((r, i) => <tr key={start + i} className="border-t border-[#EFEBE0] text-[#5D4037]" data-testid={`ph-row-${start + i}`}>{data.columns.map(([k]) => <td key={k} className="whitespace-nowrap px-3 py-2">{String(r[k] ?? "")}</td>)}</tr>)}</tbody></table></div>
      <Pager page={page} total={data.rows.length} setPage={setPage} />
    </div>
  );
}

/** Reports > Public Health: clinic-wide surveillance export as CSV or PDF, counts or a named list. */
export default function PublicHealthReport() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({ mode: "counts", start: "", end: "", codes: "" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const loadCfg = () => api.get("/public-health/settings").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { loadCfg(); }, []);
  const canList = isAdmin || cfg?.can_export_list;
  const params = (format) => ({ ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), format });
  const run = async (format) => {
    setBusy(format);
    try {
      if (format === "json") setData((await api.get("/public-health/report", { params: params("json") })).data);
      else { await downloadFile("/public-health/report", params(format), `public-health-${f.mode}.${format}`); toast.success(`${format.toUpperCase()} downloaded`); }
    } catch (e) { toast.error(e.message && !e.response ? e.message : apiError(e)); } finally { setBusy(""); }
  };
  const toggle = async (on) => {
    try { await api.put("/public-health/settings", { clinicians_can_export_list: on }); toast.success(on ? "Clinicians can now export the named list" : "Only admins can export the named list"); loadCfg(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="space-y-5 p-6" data-testid="public-health-report">
      <div><h2 className="font-heading text-xl font-bold text-[#3E2723]">Public Health Export</h2>
        <p className="mt-1 max-w-3xl text-sm text-[#6D4C41]">Clinic-wide diagnoses for a date range, as CSV or PDF. To export one diagnosis, open it on the client's Problem List and choose Generate HL7.</p></div>
      {isAdmin && cfg && <ClinicianToggle on={cfg.clinicians_can_export_list} onChange={toggle} />}
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Report type">
        {MODES.map(([k, l, hint]) => {
          const off = k === "list" && !canList;
          return (
            <button key={k} type="button" role="radio" aria-checked={f.mode === k} disabled={off} data-testid={`ph-mode-${k}`} onClick={() => { setF({ ...f, mode: k }); setData(null); }}
              className={`rounded-2xl border px-4 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${f.mode === k ? "border-[#4E342E] bg-[#4E342E] text-white" : "border-[#E5DFD3] bg-white text-[#3E2723] hover:bg-[#F5F2EB]"}`}>
              <span className="block text-sm font-semibold">{l}</span><span className={`block text-[11px] ${f.mode === k ? "text-white/80" : "text-[#7B6157]"}`}>{off ? "An admin has not allowed clinicians to export names." : hint}</span>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div><FLabel>Diagnosed from</FLabel><DateField testId="ph-start" value={f.start} noFuture onChange={(v) => setF({ ...f, start: v })} /></div>
        <div><FLabel>Diagnosed to</FLabel><DateField testId="ph-end" value={f.end} noFuture onChange={(v) => setF({ ...f, end: v })} /></div>
        <div><FLabel>ICD-10 codes or prefixes</FLabel><FInput data-testid="ph-codes" value={f.codes} placeholder="All, or for example F32, F41.1" onChange={(e) => setF({ ...f, codes: e.target.value })} /></div>
      </div>
      {f.mode === "list" && <p className="flex items-center gap-2 rounded-xl bg-[#FFF3E0] px-3 py-2 text-xs font-semibold text-[#8D5A00]" data-testid="ph-phi-warning"><ShieldAlert size={14} /> Contains protected health information. Send only through an approved public health channel.</p>}
      <div className="flex flex-wrap gap-2">
        <Btn variant="outline" data-testid="ph-preview-btn" disabled={!!busy} onClick={() => run("json")}><Eye size={14} /> {busy === "json" ? "Loading" : "Preview"}</Btn>
        <Btn variant="outline" data-testid="ph-csv-btn" disabled={!!busy} onClick={() => run("csv")}><Download size={14} /> {busy === "csv" ? "Preparing" : "Download CSV"}</Btn>
        <Btn data-testid="ph-pdf-btn" disabled={!!busy} onClick={() => run("pdf")}><FileText size={14} /> {busy === "pdf" ? "Preparing" : "Download PDF"}</Btn>
        {data && <span className="self-center text-xs text-[#7B6157]" data-testid="ph-total">{data.total} rows</span>}
      </div>
      <Preview data={data} />
    </Card>
  );
}
